import { JEFE_3 } from '../../data/boss-objects';
import { SIM_HZ } from '../../data/tuning';

/**
 * The chess board of JEFE-3 El Padrinito, phase "Plazas" (GDD §9.2, issue #121). Pure geometry and moves: 4 rows of 2.75 u
 * between the ground and the ceiling, board columns of 2.75 u fixed to the world. A rook moves vertically and a bishop
 * diagonally, one cell per move, bouncing off the top and bottom rows; the move is computed from the ticks since the last
 * one (never accumulated) and announced before it happens.
 */
export type PieceKind = 'rook' | 'bishop';

export interface PieceState {
  readonly kind: PieceKind;
  row: number;
  col: number;
  /** Direction of the next move: rows up (+1) or down (-1) and, for a bishop, board columns right (+1) or left (-1). */
  dr: number;
  dc: number;
  /** Ticks since the last move (or since it appeared). */
  sinceMove: number;
  /** The announced destination, shown highlighted until the piece moves; null while nothing is announced. */
  next: { row: number; col: number; dr: number } | null;
}

export const MOVE_TICKS = Math.round(JEFE_3.moveEverySeconds * SIM_HZ);
export const WARN_TICKS = Math.round(JEFE_3.warnSeconds * SIM_HZ);

/** World x of the centre of a board column and height of the centre of a row. */
export const cellX = (col: number): number => col * JEFE_3.cellSize;
export const cellY = (row: number): number => JEFE_3.boardBottom + (row + 0.5) * JEFE_3.cellSize;
/** The board column that contains a world x. */
export const colOfX = (x: number): number => Math.round(x / JEFE_3.cellSize);

export const createPiece = (kind: PieceKind, row: number, col: number, dr: number, dc: number): PieceState => ({ kind, row, col, dr, dc, sinceMove: 0, next: null });

/** The cell a piece moves to next: a rook one row up or down, a bishop one cell diagonally; the rows bounce at the edges. */
export function nextCell(piece: Pick<PieceState, 'kind' | 'row' | 'col' | 'dr' | 'dc'>): { row: number; col: number; dr: number } {
  let dr = piece.dr;
  if (piece.row + dr < 0 || piece.row + dr > JEFE_3.rows - 1) dr = -dr;
  return { row: piece.row + dr, col: piece.col + (piece.kind === 'bishop' ? piece.dc : 0), dr };
}

export interface PieceStep {
  announced: boolean;
  moved: boolean;
}

/**
 * One tick of a piece. `occupied(row, col)` tells whether another piece holds a cell: a blocked piece stays where it is
 * (and tries again on its next move) so two pieces never share a cell.
 */
export function stepPiece(piece: PieceState, occupied: (row: number, col: number) => boolean): PieceStep {
  const step: PieceStep = { announced: false, moved: false };
  piece.sinceMove += 1;
  if (piece.sinceMove === MOVE_TICKS - WARN_TICKS) {
    piece.next = nextCell(piece);
    step.announced = true;
  }
  if (piece.sinceMove >= MOVE_TICKS) {
    const target = piece.next ?? nextCell(piece);
    if (!occupied(target.row, target.col)) {
      piece.row = target.row;
      piece.col = target.col;
      piece.dr = target.dr;
      step.moved = true;
    }
    piece.next = null;
    piece.sinceMove = 0;
  }
  return step;
}
